import { Injectable, inject, signal } from '@angular/core';
import { DiaFestivo } from '@schemas/festivos.schema';
import { esFestivo, getNombreFestivo } from '@shared/vacaciones.utils';
import { RhFestivosService } from './rh-festivos-service';

/**
 * Días festivos: store de la aplicación.
 *
 * El transporte lo hace `RhFestivosService`, que se genera desde el contrato
 * (`@intelishs-group/schema-core`) y sabe las rutas y las formas. Lo que vive
 * aquí es lo que NO está en ningún contrato: el estado acumulado, la caché de
 * años y los dos helpers síncronos.
 *
 * Por eso este servicio no se borró al cablear el generado, a diferencia del
 * antiguo `catalog.service.ts`, que sí era transporte duplicado. Si el signal y
 * la caché se mudaran a los consumidores, el estado se duplicaría cuatro veces y
 * la caché dejaría de sobrevivir a las navegaciones.
 *
 * Los componentes llaman `cargar(anios)` en su `ngOnInit` y después leen el
 * signal `festivos` de forma reactiva.
 */
@Injectable({ providedIn: 'root' })
export class FestivosService {
  private readonly api = inject(RhFestivosService);

  // ── Estado reactivo ──────────────────────────────────────────────────────
  private readonly _festivos = signal<DiaFestivo[]>([]);
  private readonly _loading = signal(false);
  /**
   * Caché de años ya cargados en `_festivos`. Si el consumidor llama
   * `cargar([2026, 2027])` y los dos ya están aquí, no se hace fetch.
   * Se reinicia solo en este servicio (singleton root) para que persista
   * a través de navegaciones de página.
   */
  private readonly aniosCargados = new Set<number>();

  /** Lista de festivos cargados. Signal de solo lectura para los consumidores. */
  readonly festivos = this._festivos.asReadonly();

  /** true mientras se está cargando la lista de festivos. */
  readonly loading = this._loading.asReadonly();

  /**
   * Carga festivos de los años indicados y los almacena en el signal
   * `festivos`. Si TODOS los años ya están en caché (por una llamada previa en
   * otra página), no hace fetch — los componentes recargados usan de inmediato
   * la lista que ya tenía el servicio.
   *
   * Los años faltantes viajan en UNA sola petición
   * (`GET /api/Festivos?anios=2026&anios=2027`). El parámetro es un query
   * REPETIBLE y por eso se pasa como arreglo: `HttpClient` repite la clave por
   * elemento. Unirlo con comas devuelve `400 The value '2026,2027' is not
   * valid.` — el generador lo emitía así hasta que se corrigió, y era la razón
   * por la que este cableado estuvo bloqueado.
   *
   * @example
   * const anio = new Date().getFullYear();
   * this.festivosService.cargar([anio, anio + 1]);
   */
  cargar(anios: number[]): void {
    const faltantes = anios.filter((a) => !this.aniosCargados.has(a));
    if (faltantes.length === 0) return; // todo en caché, no fetcheamos.

    this._loading.set(true);
    this.api
      .getFestivos(faltantes)
      .then((data) => {
        // Append a la lista existente; no perdemos años cargados antes.
        this._festivos.update((prev) => [...prev, ...(data ?? [])]);
        for (const a of faltantes) this.aniosCargados.add(a);
        this._loading.set(false);
      })
      .catch(() => this._loading.set(false));
  }

  // ── Helpers síncronos (usan el signal cargado) ───────────────────────────

  /** true si la fecha es un festivo de la lista cargada. */
  esFestivo(fecha: Date): boolean {
    return esFestivo(fecha, this._festivos());
  }

  /** Nombre del festivo si coincide, undefined si no. */
  getNombre(fecha: Date): string | undefined {
    return getNombreFestivo(fecha, this._festivos());
  }
}
